import React, {useContext, useEffect, useState} from "react";
import Scene from "./Scene";
import {BD} from "../BD";
import "./Editor.css";
import Galleries from "./Galleries";
import Exporter from "./Exporter";
import { DraftManager } from "./DraftManager";
import Button from "../Button";
import NewsArticleScraper from "./NewsArticleScraper";
import { ScrapeResponse } from "../../services/editor";
import { getPostByIdForEdit } from "../../services/posts";
import { RESTPost } from "../../services/types";
import { useAuthenticatedAction } from "../../hooks/useAuthenticatedAction";
import { UserContext } from "../../contexts/UserContext";
import { useNavigate } from "react-router-dom";

interface EditorProps {

}

const Editor:React.FC<EditorProps> = () => {
    // Detect edit mode from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const editPostId = urlParams.get('edit');
    const isEditMode = editPostId !== null;

    const { currentUser, isModerator } = useContext(UserContext);
    const navigate = useNavigate();

    const savedState = DraftManager.loadDraft();
    const initialBdDef = savedState?.bdDef || new BD().definition;
    const initialSelectedHolder = savedState?.selectedHolder || 'bird1';
    const initialSelectedGallery = savedState?.selectedGallery || 'bird';

    const [bdDef, setBdDef] = useState(initialBdDef);
    const [selectedHolder, setSelectedHolder] = useState(initialSelectedHolder);
    const [selectedGallery, setSelectedGallery] = useState(initialSelectedGallery);
    const [sceneFormat, setSceneFormat] = useState('standard');
    const [showExporter, setShowExporter] = useState(false);
    const [editPost, setEditPost] = useState<RESTPost | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editIsCertified, setEditIsCertified] = useState(false);
    const [editIsHidden, setEditIsHidden] = useState(false);
    const [editIsVerbi, setEditIsVerbi] = useState(false);
    const [editVerbiWord, setEditVerbiWord] = useState('');
    const [editAuthorId, setEditAuthorId] = useState<number | undefined>(undefined);
    const [permissionError, setPermissionError] = useState<string | null>(null);

    // Use authenticated action for loading post data
    const { execute: loadPost, loading: loadingPost, error: loadingError } = useAuthenticatedAction<RESTPost, [number]>();

    useEffect(() => {
        if( selectedHolder.startsWith('bird') ) setSelectedGallery('bird');
        else if( selectedHolder.startsWith('bubble') ) setSelectedGallery('bubble');
        else setSelectedGallery('background');
    }, [selectedHolder]);

    useEffect(() => {
        DraftManager.saveDraft({ bdDef, selectedHolder, selectedGallery });
    }, [bdDef, selectedHolder, selectedGallery]);

    useEffect(() => {
        if(bdDef.background.startsWith('sq_')){
            setSceneFormat('square');
        }else if(bdDef.background === 'news'){
            setSceneFormat('news');
        }else{
            setSceneFormat('standard');
        }
    }, [bdDef.background]);

    // Load post data in edit mode
    useEffect(() => {
        if (isEditMode && editPostId) {
            // Check if user is logged in
            if (!currentUser) {
                setPermissionError('Vous devez être connecté pour modifier une BD.');
                return;
            }

            loadPost(getPostByIdForEdit, parseInt(editPostId))
                .then((post) => {
                    if (post) {
                        // Check permissions
                        const isAuthor = currentUser.id === post.author;
                        const isModeratorUser = isModerator();
                        
                        if (!isAuthor && !isModeratorUser) {
                            setPermissionError('Vous n\'êtes pas autorisé à modifier cette BD.');
                            return;
                        }

                        // If author (not moderator), check additional conditions
                        if (isAuthor && !isModeratorUser) {
                            const hasOrthoCorrection = post.meta.ortho_correction && post.meta.ortho_correction.trim() !== '';
                            const postDate = new Date(post.date || '');
                            const now = new Date();
                            const minutesSincePost = (now.getTime() - postDate.getTime()) / (1000 * 60);
                            const isWithin15Minutes = minutesSincePost <= 15;

                            if (!hasOrthoCorrection && !isWithin15Minutes) {
                                setPermissionError('Vous ne pouvez plus modifier cette BD. Le délai de 15 minutes est dépassé.');
                                return;
                            }
                        }

                        // User has permission, proceed with loading
                        setEditPost(post);
                        setEditTitle(post.title.raw || post.title.rendered);
                        setEditDescription(post.content.raw || post.content.rendered);
                        setEditIsCertified(post.meta.certified || false);
                        setEditIsHidden(post.meta.hidden || false);
                        setEditAuthorId(post.author);
                        const isVerbi = post.categories.includes(7615); // 7615 is Verbi category
                        setEditIsVerbi(isVerbi);
                        if (isVerbi && post.verbi?.verbi) {
                            setEditVerbiWord(post.verbi.verbi);
                        }
                        
                        // Extract BD definition from post meta
                        if (post.meta.definition) {
                            try {
                                const bdDefinition = JSON.parse(post.meta.definition);
                                setBdDef(bdDefinition);
                            } catch (parseError) {
                                throw new Error('Invalid BD definition format in post meta. Cannot edit this post.');
                            }
                        } else {
                            throw new Error('No BD definition found in post meta. Cannot edit this post.');
                        }
                    }
                })
                .catch((error) => {
                    console.error('Failed to load post for editing:', error);
                });
        }
    }, [isEditMode, editPostId, loadPost, currentUser, isModerator]);

    const handleGalleryClick = (itemId:string) => {
        changeElement(selectedHolder, {type:itemId});
    }

    const changeElement = (elementId:string, newValue:any) => {
        switch (elementId) {
            case 'separator' : {
                setBdDef({
                    ...bdDef,
                    [newValue.sepId]: newValue.value
                })
                break;
            }
            case 'background': {
                setBdDef({
                    ...bdDef,
                    background: newValue.type
                })
                break;
            }
            default: {
                setBdDef({
                    ...bdDef,
                    [elementId]: {
                        ...bdDef[elementId],
                        ...newValue
                    }
                });
                break;
            }
        }
    }

    const handleReset = () => {
        const fresh = new BD().definition;
        setBdDef(fresh);
        setSelectedHolder('bird1');
        setSelectedGallery('bird');
        setSceneFormat('standard');
    }

    const handleArticleScraped = (scrapedData: ScrapeResponse) => {
        if (scrapedData.success && scrapedData.og_data) {
            const newsArticle = {
                url: scrapedData.url,
                source: scrapedData.og_data.site_name || '',
                title: scrapedData.og_data.title || '',
                image: scrapedData.og_data.image || '',
                summary: scrapedData.og_data.description || ''
            };

            setBdDef({
                ...bdDef,
                newsArticle
            });
        }
    }

    if (loadingPost) {
        return (
            <div id={"bd_editor"} className={"Editor"}>
                <div className={"editor_header"}>
                    <h3>Chargement...</h3>
                </div>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    Chargement de la BD à modifier...
                </div>
            </div>
        );
    }

    if (loadingError || permissionError) {
        return (
            <div id={"bd_editor"} className={"Editor"}>
                <div className={"editor_header"}>
                    <h3>Erreur</h3>
                </div>
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <div style={{ color: 'red', marginBottom: '1rem' }}>
                        ❌ {loadingError || permissionError}
                    </div>
                    <Button variant="secondary" size="medium" onClick={() => navigate('/')}>
                        Retour à l'accueil
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div id={"bd_editor"} className={"Editor"}>
            <div className={"editor_header"}>
                <h3>{isEditMode ? 'Modifier ma BD' : 'Nouvelle BD'}</h3>
                <div className={"tools"}>
                    <Button variant="secondary" size="medium" onClick={handleReset}>
                        Réinitialiser
                    </Button>
                    <Button variant="primary" size="medium" onClick={() => setShowExporter(true)}>
                        J'ai terminé ma BD
                    </Button>
                </div>
            </div>
            {sceneFormat === 'news' && (
                <NewsArticleScraper 
                    onArticleScraped={handleArticleScraped} 
                    initialUrl={bdDef.newsArticle?.url || ''}
                />
            )}
            <div className={"SceneWrapper " + sceneFormat}>
                <Scene format={sceneFormat} bdDef={bdDef} selectedHolder={selectedHolder} onSelectHolder={(h) => setSelectedHolder(h)} onChangeElement={changeElement}/>
            </div>
            <Galleries selectedGallery={selectedGallery} onSelectItem={handleGalleryClick}/>
            {
                showExporter &&
                <Exporter 
                    bdDef={bdDef} 
                    onClose={() => setShowExporter(false)}
                    onPublishSuccess={(postId) => {
                        setShowExporter(false);
                        navigate(`/post?p=${postId}`);
                    }}
                    editMode={{
                        isEditMode,
                        postId: editPostId ? parseInt(editPostId) : undefined,
                        authorId: editAuthorId,
                        title: editTitle,
                        description: editDescription,
                        isCertified: editIsCertified,
                        isHidden: editIsHidden,
                        isVerbi: editIsVerbi,
                        verbiWord: editVerbiWord
                    }}
                />
            }
        </div>
    );
}

export default Editor;